import React, { useState } from 'react';
import PlannerIcons from '../../components/shared/PlannerIcons';

export default function ProjectChecklist({ project, updateProject }) {
  const [newTask, setNewTask] = useState('');
  const [newCat, setNewCat] = useState('6-8 Months');
  const [newAssigned, setNewAssigned] = useState('');
  const [newReminder, setNewReminder] = useState(''); 
  const [draggingId, setDraggingId] = useState(null); 

  const tasks = project.tasks || [];
  const categories = [...new Set(tasks.map(t => t.category)), '12+ Months', '9-11 Months', '6-8 Months', '3-5 Months', '1-2 Months', 'Week Of'].filter((v, i, a) => a.indexOf(v) === i);

  // Drag and Drop Handlers
  const handleDragStart = (e, task) => {
    e.dataTransfer.setData('text/plain', task.id);
    setDraggingId(task.id);
  };

  const handleDropTask = (e, targetTask) => {
    e.preventDefault();
    e.stopPropagation();
    const sourceId = e.dataTransfer.getData('text/plain');
    if (sourceId === targetTask.id) return;

    const newTasks = [...tasks];
    const sourceIndex = newTasks.findIndex(t => t.id === sourceId);
    const targetIndex = newTasks.findIndex(t => t.id === targetTask.id);

    if (sourceIndex === -1 || targetIndex === -1) return;

    const [movedTask] = newTasks.splice(sourceIndex, 1);
    movedTask.category = targetTask.category; 
    
    newTasks.splice(targetIndex, 0, movedTask);
    updateProject(project.id, { tasks: newTasks });
    setDraggingId(null);
  };

  const handleDropCategory = (e, targetCategory) => {
    e.preventDefault();
    const sourceId = e.dataTransfer.getData('text/plain');
    if (!sourceId) return;

    const newTasks = [...tasks];
    const sourceIndex = newTasks.findIndex(t => t.id === sourceId);
    
    if (sourceIndex === -1) return;

    const [movedTask] = newTasks.splice(sourceIndex, 1);
    movedTask.category = targetCategory;
    newTasks.push(movedTask);
    
    updateProject(project.id, { tasks: newTasks });
    setDraggingId(null);
  };

  // Common Wedding Tasks Template
  const commonWeddingTasks = [
      { text: 'Determine budget and how expenses will be shared', category: '12+ Months' },
      { text: 'Draft guest list', category: '12+ Months' },
      { text: 'Reserve date and venues', category: '12+ Months' },
      { text: 'Book officiant', category: '9-11 Months' },
      { text: 'Hire photographer/videographer', category: '9-11 Months' },
      { text: 'Hire caterer', category: '9-11 Months' },
      { text: 'Purchase dress', category: '9-11 Months' },
      { text: 'Book musicians/DJ', category: '6-8 Months' },
      { text: 'Order bridesmaid dresses', category: '6-8 Months' },
      { text: 'Send save-the-date cards', category: '6-8 Months' },
      { text: 'Order wedding cake', category: '3-5 Months' },
      { text: 'Purchase wedding bands', category: '3-5 Months' },
      { text: 'Mail wedding invitations', category: '1-2 Months' },
      { text: 'Apply for marriage license', category: '1-2 Months' },
      { text: 'Provide caterer/venue with final guest count', category: 'Week Of' },
      { text: 'Confirm arrival times with vendors', category: 'Week Of' }
  ];

  const handleAutoPopulate = () => {
      if (tasks.length > 0 && !window.confirm("This will add standard wedding tasks to your existing list. Continue?")) return;
      
      const newTasks = commonWeddingTasks.map((t, i) => ({
          id: Date.now().toString() + '_' + i,
          text: t.text,
          category: t.category,
          completed: false,
          assignedTo: ''
      }));
      
      updateProject(project.id, { tasks: [...tasks, ...newTasks] });
  };

  const toggleTask = (taskId) => {
    const updated = tasks.map(t => t.id === taskId ? { ...t, completed: !t.completed } : t);
    updateProject(project.id, { tasks: updated });
  };

  const addTask = (e) => {
    e.preventDefault();
    if(!newTask.trim()) return;
    const task = { id: Date.now().toString(), text: newTask, category: newCat, completed: false, assignedTo: newAssigned, reminderDate: newReminder };
    updateProject(project.id, { tasks: [...tasks, task] });
    setNewTask('');
    setNewAssigned('');
    setNewReminder('');
  };

  const removeTask = (taskId) => {
    updateProject(project.id, { tasks: tasks.filter(t => t.id !== taskId) });
  };

  const groupedTasks = categories.reduce((acc, cat) => {
    const catTasks = tasks.filter(t => t.category === cat);
    if (catTasks.length > 0 || ['12+ Months', '9-11 Months', '6-8 Months', '3-5 Months', '1-2 Months', 'Week Of'].includes(cat)) {
        acc[cat] = catTasks;
    }
    return acc;
  }, {});

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col">
      <div className="p-4 md:p-6 border-b border-gray-100 bg-gray-50/50 flex flex-col gap-4">
        <div className="flex justify-between items-center">
            <p className="text-sm font-bold text-gray-600 uppercase tracking-wider">Task Manager</p>
            <button type="button" onClick={handleAutoPopulate} className="text-xs bg-purple-100 text-purple-700 hover:bg-purple-200 px-3 py-1.5 rounded-lg font-bold transition shadow-sm flex items-center gap-1">
                <span>✨</span> Auto-Populate Wedding Plan
            </button>
        </div>
        <form onSubmit={addTask} className="flex flex-col sm:flex-row gap-3">
          <select value={newCat} onChange={e => setNewCat(e.target.value)} className="w-full sm:w-40 border-gray-300 rounded-lg text-sm shadow-sm focus:ring-pink-500 focus:border-pink-500 p-2.5 font-medium bg-white">
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <input 
            type="text" 
            value={newTask} 
            onChange={e => setNewTask(e.target.value)} 
            placeholder="Add a new task..." 
            className="flex-1 border-gray-300 rounded-lg text-sm shadow-sm focus:ring-pink-500 focus:border-pink-500 p-2.5 border"
          />
          <input 
            type="text" 
            value={newAssigned} 
            onChange={e => setNewAssigned(e.target.value)} 
            placeholder="Assign to..." 
            className="w-full sm:w-32 border-gray-300 rounded-lg text-sm shadow-sm focus:ring-pink-500 focus:border-pink-500 p-2.5 border"
          />
          <input 
            type="date" 
            value={newReminder} 
            onChange={e => setNewReminder(e.target.value)} 
            className="w-full sm:w-36 border-gray-300 rounded-lg text-sm shadow-sm focus:ring-pink-500 focus:border-pink-500 p-2.5 border bg-white text-gray-500"
            title="Reminder Date"
          />
          <button type="submit" className="w-full sm:w-auto bg-pink-600 hover:bg-pink-700 text-white px-6 py-2.5 rounded-lg font-bold text-sm transition shadow-sm">Add Task</button>
        </form>
      </div>

      <div className="p-4 md:p-6 space-y-8">
        {Object.entries(groupedTasks).map(([category, catTasks]) => {
          const completed = catTasks.filter(t => t.completed).length;
          return (
            <div 
                key={category} 
                className={`space-y-3 p-2 -mx-2 rounded-xl transition-colors ${draggingId ? 'bg-gray-50/50 border-2 border-dashed border-transparent hover:border-pink-300' : 'border-2 border-transparent'}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => handleDropCategory(e, category)}
            >
              <div className="flex items-center gap-3 md:gap-4">
                <h3 className="font-serif text-base md:text-lg font-bold text-gray-900 whitespace-nowrap">{category}</h3>
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-[10px] md:text-xs font-bold text-gray-500 bg-gray-100 px-2 py-1 rounded-full whitespace-nowrap">{completed} / {catTasks.length}</span>
              </div>
              <div className="space-y-2 md:pl-2 min-h-[40px]">
                {catTasks.length === 0 && <p className="text-xs text-gray-400 italic py-2">Drag tasks here...</p>}
                {catTasks.map(task => (
                  <div 
                    key={task.id} 
                    draggable
                    onDragStart={(e) => handleDragStart(e, task)}
                    onDragEnd={() => setDraggingId(null)}
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onDrop={(e) => handleDropTask(e, task)}
                    className={`flex items-start gap-3 md:gap-4 p-3 md:p-4 rounded-lg border transition-all duration-200 group cursor-grab active:cursor-grabbing ${task.completed ? 'bg-gray-50 border-gray-200 opacity-75' : 'bg-white border-gray-200 hover:border-pink-300 hover:shadow-sm'} ${draggingId === task.id ? 'opacity-50 border-dashed border-pink-400 shadow-md' : ''}`}
                  >
                    <div className="mt-1 text-gray-400 opacity-50 hover:opacity-100 cursor-grab hidden sm:block">⋮⋮</div>
                    <button 
                      onClick={() => toggleTask(task.id)}
                      className={`mt-0.5 w-5 h-5 md:w-6 md:h-6 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${task.completed ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300 group-hover:border-pink-400 bg-white'}`}
                    >
                      {task.completed && <PlannerIcons.Check />}
                    </button>
                    <span className={`flex-1 text-sm md:text-base font-medium transition-colors ${task.completed ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                      {task.text}
                      {task.assignedTo && (
                          <span className="ml-2 text-[10px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-bold inline-flex items-center gap-1">
                              👤 {task.assignedTo}
                          </span>
                      )}
                      {task.reminderDate && (
                          <span className={`ml-2 text-[10px] px-2 py-0.5 rounded-full font-bold inline-flex items-center gap-1 ${new Date(task.reminderDate) <= new Date() && !task.completed ? 'bg-orange-100 text-orange-700 border border-orange-200 animate-pulse' : 'bg-gray-100 text-gray-600 border border-gray-200'}`} title="Reminder Set">
                              🔔 {new Date(task.reminderDate).toLocaleDateString(undefined, {month:'short', day:'numeric'})}
                          </span>
                      )}
                    </span>
                    <button 
                      onClick={() => removeTask(task.id)}
                      className="opacity-100 sm:opacity-0 group-hover:opacity-100 p-1 md:p-2 text-red-400 hover:text-red-600 transition-opacity bg-red-50 rounded-full"
                    >
                      <PlannerIcons.Trash />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
        {Object.keys(groupedTasks).length === 0 && (
          <div className="text-center py-12 md:py-20 text-gray-400 flex flex-col items-center border-2 border-dashed border-gray-200 rounded-xl bg-gray-50/50">
            <div className="text-3xl md:text-4xl mb-3">📝</div>
            <p className="text-sm md:text-base font-medium">Your checklist is empty.</p>
            <p className="text-xs md:text-sm mt-1 mb-4">Add tasks above to stay organized.</p>
            <button onClick={handleAutoPopulate} className="bg-purple-100 text-purple-700 px-4 py-2 rounded-lg font-bold text-sm hover:bg-purple-200 transition shadow-sm">
                ✨ Use Wedding Template
            </button>
          </div>
        )}
      </div>
    </div>
  );
}